import { TFunction } from "i18next";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { ExpandedColumn } from "library/types/columns";
import { ProcessedTaxonomyState } from "library/types/types";
import {
  ExportPeriod,
  getExportFileName,
  getOfferExportValue,
  isNumberColumn,
} from "./exportOffers";

pdfMake.addVirtualFileSystem(pdfFonts);

const HEADER_FILL = "#005aa1";
const HEADER_COLOR = "#ffffff";
const INVALIDATED_FILL = "#fed9cc";
const ZEBRA_FILL = "#f3f2f1";

const BODY_FONT_SIZE = 8;
const HEADER_FONT_SIZE = 8;
const CELL_PADDING_H = 2;
const CELL_PADDING_V = 1;
// a cell text longer than this wraps onto further lines (at spaces)
const MAX_CELL_TEXT_WIDTH = 170;
// a single word is never wrapped below this width; longer words get broken by pdfmake
const MAX_WORD_WIDTH = 260;
const MIN_COLUMN_WIDTH = 24;
const PAGE_MARGIN = 20;
const MIN_PAGE_WIDTH = 842; // A4 landscape
const MAX_PAGE_HEIGHT = 1200;

/**
 * Approximate rendered width of a text in Roboto: glyph widths per 1000 em,
 * grouped by character class (uppercase and digits are much wider than
 * lowercase, spaces and punctuation are narrow).
 */
const estimateTextWidth = (
  text: string,
  fontSize: number,
  bold = false,
): number => {
  let em = 0;
  for (const ch of text) {
    if (ch === " ") em += 250;
    else if (/[0-9]/.test(ch)) em += 570;
    else if (/[A-ZĀČĒĢĪĶĻŅŠŪŽ]/.test(ch)) em += 680;
    else if (/[a-zāčēģīķļņšūž]/.test(ch)) em += 540;
    else if (/[.,:;'`|!-]/.test(ch)) em += 280;
    else em += 600;
  }
  // 5% safety margin: an estimate that is too small makes pdfmake break the word onto a second line
  return (em / 1000) * fontSize * (bold ? 1.05 : 1) * 1.05;
};

const words = (text: string): string[] => text.split(/\s+/).filter(Boolean);

const longestWordWidth = (text: string, fontSize: number, bold = false) =>
  words(text).reduce(
    (max, w) => Math.max(max, estimateTextWidth(w, fontSize, bold)),
    0,
  );

/**
 * Column width from its content: the longest cell text up to a cap (longer
 * texts wrap at spaces), but never narrower than the longest single word of
 * any cell or of the header, so words do not get broken and the header can
 * still wrap onto two lines.
 */
const contentWidth = (header: string, cellTexts: string[]): number => {
  let longestCell = 0;
  let longestWord = longestWordWidth(header, HEADER_FONT_SIZE, true);
  for (const text of cellTexts) {
    longestCell = Math.max(longestCell, estimateTextWidth(text, BODY_FONT_SIZE));
    longestWord = Math.max(longestWord, longestWordWidth(text, BODY_FONT_SIZE));
  }
  // pdfmake column widths exclude the cell padding; the padding is added to the page width instead
  const width =
    Math.max(
      Math.min(longestCell, MAX_CELL_TEXT_WIDTH),
      Math.min(longestWord, MAX_WORD_WIDTH),
    ) + 2;
  return Math.ceil(Math.max(width, MIN_COLUMN_WIDTH));
};

/**
 * Builds a landscape PDF listing the given offers, one table column per
 * visible grid column. Columns are as narrow as their content allows (like the
 * Excel export) and the page hugs the table width, so wide views still stay on
 * a single page horizontally, like in the other GrECo apps.
 */
export const createPDFOffers = ({
  rows,
  columns,
  taxonomy,
  t,
  invalidatedStatusId,
  period,
}: {
  rows: any[];
  columns: ExpandedColumn[];
  taxonomy: ProcessedTaxonomyState | null;
  t: TFunction;
  invalidatedStatusId?: number;
  /** export period, becomes part of the document title / file name */
  period?: ExportPeriod;
}) => {
  const headerTexts = columns.map((c) => t(c.labelKey));

  // cell texts first: they drive the column widths
  const cellTexts: string[][] = rows.map((row) =>
    columns.map((c) => {
      const value = getOfferExportValue({
        column: c,
        row,
        taxonomy,
        t,
        target: "pdf",
      });
      return value === null || value === undefined ? "" : String(value);
    }),
  );

  const widths = columns.map((c, colIndex) =>
    contentWidth(
      headerTexts[colIndex] ?? "",
      cellTexts.map((r) => r[colIndex] ?? ""),
    ),
  );
  const totalWidth = widths.reduce((sum, w) => sum + w, 0);

  const header = headerTexts.map((text) => ({
    text,
    style: "headerCell",
  }));

  const body = cellTexts.map((texts, rowIndex) => {
    const row = rows[rowIndex];
    const isInvalidated =
      invalidatedStatusId !== undefined &&
      row?.OfferStatus === invalidatedStatusId;
    // offers marked as error are shaded like invalidated ones
    const fillColor = isInvalidated || row?.IsMarkedAsError
      ? INVALIDATED_FILL
      : rowIndex % 2 === 1
        ? ZEBRA_FILL
        : undefined;

    return texts.map((text, colIndex) => ({
      text,
      style: "bodyCell",
      alignment: isNumberColumn(columns[colIndex] as ExpandedColumn)
        ? "right"
        : "left",
      fillColor,
    }));
  });

  // page hugs the table: column widths plus the cell paddings and border lines pdfmake adds per column; kept landscape
  const pageWidth = Math.max(
    Math.ceil(
      totalWidth +
        columns.length * (2 * CELL_PADDING_H + 1) +
        2 * PAGE_MARGIN,
    ),
    MIN_PAGE_WIDTH,
  );
  const pageHeight = Math.min(MAX_PAGE_HEIGHT, Math.round(pageWidth * 0.707));

  const docDefinition: any = {
    pageOrientation: "landscape",
    pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN],
    pageSize: {
      width: pageWidth,
      height: pageHeight,
    },
    info: {
      title: getExportFileName("pdf", period),
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths,
          body: [header, ...body],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#c8c6c4",
          vLineColor: () => "#c8c6c4",
          paddingLeft: () => CELL_PADDING_H,
          paddingRight: () => CELL_PADDING_H,
          paddingTop: () => CELL_PADDING_V,
          paddingBottom: () => CELL_PADDING_V,
        },
      },
    ],
    styles: {
      headerCell: {
        fillColor: HEADER_FILL,
        color: HEADER_COLOR,
        bold: true,
        alignment: "center",
        fontSize: HEADER_FONT_SIZE,
      },
      bodyCell: {
        color: "#000000",
        fontSize: BODY_FONT_SIZE,
      },
    },
    defaultStyle: {
      font: "Roboto",
    },
  };

  return pdfMake.createPdf(docDefinition);
};
